import { type FC, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { useServerVars } from '../shared/ServerVarsContext.tsx';
import classnames from 'classnames';

import ProjectHeader from './ProjectHeader.tsx';

import styles from './ProjectOverviewContainer.scss';

import { type ProjectOverviewContainer_searchResult$key } from './__generated__/ProjectOverviewContainer_searchResult.graphql.ts';

const COLLAPSED_CONTENT_HEIGHT = 120;

type Props = {
    openModal: (currentUrl: string) => void;
    searchResult: ProjectOverviewContainer_searchResult$key | null | undefined;
};

const ProjectOverviewContainer: FC<Props> = props => {
    const { openModal, searchResult: searchResultRef } = props;

    const searchResult = useFragment(
        graphql`
            fragment ProjectOverviewContainer_searchResult on TradeInteriorProjectQueryOutput {
                project {
                    overview
                    tradeCoverPhoto {
                        url
                    }
                }
                ...ProjectHeader_searchResult
            }
        `,
        searchResultRef
    );

    const { isMobile } = useServerVars();

    const tradeCoverPhoto = searchResult?.project?.tradeCoverPhoto;

    const overviewParagraphs = useMemo(
        () =>
            (searchResult?.project?.overview || []).map((line, index) => {
                return (
                    <p
                        key={`overview-line-${index}`}
                        data-tn="project-description"
                        className={styles.paragraph}
                    >
                        {line}
                    </p>
                );
            }),
        [searchResult]
    );

    const hasOverviewParagraphs = overviewParagraphs.length > 0;

    return (
        <div className={styles.wrapper}>
            <ProjectHeader searchResult={searchResult} />
            {hasOverviewParagraphs && (
                <>
                    {isMobile ? (
                        <ExpandingAreaWithReadMore
                            collapsedHeight={COLLAPSED_CONTENT_HEIGHT}
                            showGradient={false}
                            readMore={
                                <FormattedMessage
                                    id="abt.interiorProject.readMore"
                                    defaultMessage="Read More"
                                />
                            }
                        >
                            {overviewParagraphs}
                        </ExpandingAreaWithReadMore>
                    ) : (
                        overviewParagraphs
                    )}
                </>
            )}
            <Button
                className={classnames(styles.cta, { [styles.addMarginTop]: hasOverviewParagraphs })}
                dataTn="project-view-gallery"
                onClick={() => openModal(tradeCoverPhoto?.url || '')}
                size={isMobile ? 'medium' : undefined}
            >
                <FormattedMessage
                    id="abt.interiorProject.viewGallery"
                    defaultMessage="View project gallery"
                />
            </Button>
        </div>
    );
};

export default ProjectOverviewContainer;
